import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Filter, Wand2, X, Play, History, CheckCircle2, AlertCircle, Clock, RefreshCw } from "lucide-react";
import {
  TRIGGERS, TRIGGER_MAP, ACTIONS, ACTION_MAP, OPS, FIELDS, Zap,} from "./automations/constants";
import { FlowTree } from "./automations/FlowTree";
import i18n from "@/i18n";

/**
 * Full-page automation editor.
 * Route: /app/automations/:id ("new" for creation)
 * All state, actions, conditions and triggers live here.
 */
export default function AutomationEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(isNew ? null : id);

  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [triggerType, setTriggerType] = useState("client_created");
  const [triggerConfig, setTriggerConfig] = useState({});
  const [conditions, setConditions] = useState([]);
  const [actions, setActions] = useState([{ type: "wait", config: {}, delay_value: 60, delay_unit: "minutes" }]);
  const [tags, setTags] = useState([]);
  const [testing, setTesting] = useState(false);
  // Cronologia attività — job history for this specific automation
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);

  useEffect(() => { api.get("/tags").then((r) => setTags(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (isNew) return;
    api.get(`/automations/${id}`)
      .then((r) => {
        const a = r.data;
        setName(a.name || "");
        setEnabled(a.enabled ?? true);
        setTriggerType(a.trigger?.type || "client_created");
        setTriggerConfig(a.trigger?.config || {});
        setConditions(a.conditions || []);
        setActions(a.actions || []);
      })
      .catch(() => { toast.error("Automazione non trovata"); nav("/app/automations"); })
      .finally(() => setLoading(false));
  }, [id, isNew, nav]);

  const T = TRIGGER_MAP[triggerType];

  const addCond = () => setConditions([...conditions, { field: "client.tags", op: "contains", value: "" }]);
  const updateCond = (i, patch) => setConditions(conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const removeCond = (i) => setConditions(conditions.filter((_, idx) => idx !== i));

  const addAction = () => setActions([...actions, { type: "wait", config: {}, delay_value: 30, delay_unit: "minutes" }]);
  const updateAction = (i, patch) => setActions(actions.map((a, idx) => idx === i ? { ...a, ...patch } : a));
  const removeAction = (i) => setActions(actions.filter((_, idx) => idx !== i));

  const submit = async (asDraft = false) => {
    if (!name.trim()) { toast.error("Nome richiesto"); return; }
    if (actions.length === 0) { toast.error("Aggiungi almeno un'azione"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        enabled: asDraft ? false : enabled,
        trigger: { type: triggerType, config: triggerConfig },
        conditions, actions,
      };
      let doc;
      if (isNew && !savedId) {
        const r = await api.post("/automations", payload);
        doc = r.data;
        setSavedId(doc.id);
        window.history.replaceState(null, "", `/app/automations/${doc.id}`);
      } else {
        const r = await api.patch(`/automations/${savedId || id}`, payload);
        doc = r.data;
      }
      toast.success(asDraft ? "Salvata come bozza" : "Salvata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!savedId) { toast.error("Salva prima l'automazione"); return; }
    setTesting(true);
    try {
      const clients = (await api.get("/clients")).data;
      if (!clients.length) { toast.error("Nessun cliente disponibile"); return; }
      const cli = clients[0];
      const r = await api.post("/automations/test-trigger", {
        trigger_type: triggerType,
        context: {
          client: cli,
          appointment: { id: "test", start: new Date(Date.now() + 3600_000).toISOString(), status: "scheduled", service_id: "test" },
          invoice: { id: "test", total: 150, payment_method: "cash" },
          added_tag: "test",
        },
      });
      toast.success(`Trigger inviato · ${r.data.actions_enqueued} azion${r.data.actions_enqueued === 1 ? "e" : "i"} in coda su cliente "${cli.name}"`);
      // Ricarica cronologia dopo test
      setTimeout(loadJobs, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setTesting(false); }
  };

  const loadJobs = async () => {
    if (!savedId) return;
    setJobsLoading(true);
    try {
      const r = await api.get(`/jobs/recent?automation_id=${savedId}&limit=50`);
      setJobs(r.data || []);
    } catch (e) { /* silent */ }
    setJobsLoading(false);
  };
  useEffect(() => { loadJobs(); /* eslint-disable-next-line */ }, [savedId]);
  // Auto-refresh cronologia ogni 20s
  useEffect(() => {
    if (!savedId) return;
    const t = setInterval(loadJobs, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [savedId]);

  if (loading) return (
    <div className="sp-card p-24 text-center text-[color:var(--sp-text-muted)]">
      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
    </div>
  );

  return (
    <div data-testid="auto-editor-page" className="space-y-6">
      <header className="flex items-center gap-4 flex-wrap">
        <button onClick={() => nav("/app/automations")} className="sp-btn-ghost inline-flex items-center gap-1 text-sm" data-testid="auto-back">
          <ArrowLeft className="h-4 w-4" /> Torna
        </button>
        <div className="flex-1 min-w-[280px]">
          <input
            data-testid="auto-name"
            className="text-2xl font-display font-black bg-transparent border-0 outline-none focus:ring-0 w-full placeholder:text-[color:var(--sp-text-muted)]"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder={isNew ? "Nome automazione (es. Reminder 24h)" : ""}
          />
        </div>
        <label className="text-sm font-bold inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4" data-testid="auto-enabled" />
          Attiva
        </label>
        {savedId && (
          <button data-testid="auto-run-test" onClick={runTest} disabled={testing} className="sp-btn-ghost inline-flex items-center gap-2 text-sm">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Test
          </button>
        )}
        <button data-testid="auto-save-draft" onClick={() => submit(true)} disabled={saving} className="sp-btn-ghost inline-flex items-center gap-2 text-sm">
          Salva bozza
        </button>
        <button data-testid="auto-save" onClick={() => submit(false)} disabled={saving} className="sp-btn-primary inline-flex items-center gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
        </button>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form column */}
        <div className="lg:col-span-3 space-y-6">
          <div className="sp-card p-5">
            <label className="text-sm font-bold flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> Quando succede…</label>
            <div className="mt-3 grid grid-cols-2 gap-2" data-testid="trigger-grid">
              {TRIGGERS.map((t) => {
                const Icon = t.icon;
                const active = t.key === triggerType;
                return (
                  <button
                    key={t.key} type="button"
                    data-testid={`trigger-opt-${t.key}`}
                    onClick={() => { setTriggerType(t.key); setTriggerConfig({}); }}
                    className={`p-3 rounded-xl border text-left transition-all ${active ? "border-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/5 shadow-sm" : "border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]/40 bg-white"}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${active ? "bg-[color:var(--sp-brand)] text-white" : "bg-[color:var(--sp-subtle)] text-[color:var(--sp-text-secondary)]"}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-xs font-bold">{i18n.t(t.label)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            {T?.filter && <TriggerFilters filter={T.filter} value={triggerConfig} onChange={setTriggerConfig} tags={tags} />}
          </div>

          <div className="sp-card p-5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold flex items-center gap-1"><Filter className="h-3.5 w-3.5" /> Solo se…</label>
              <button type="button" data-testid="add-condition-btn" onClick={addCond} className="text-xs font-bold text-[color:var(--sp-brand)]">+ Aggiungi condizione</button>
            </div>
            {conditions.length === 0 ? (
              <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-2 italic">Nessun filtro — l&apos;automazione parte sempre.</div>
            ) : (
              <div className="mt-3 space-y-2">
                {conditions.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 rounded-xl bg-[color:var(--sp-subtle)]/40">
                    <select className="sp-input col-span-5 !py-1 text-xs" value={c.field} onChange={(e) => updateCond(i, { field: e.target.value })}>
                      {FIELDS.map((f) => <option key={f.key} value={f.key}>{i18n.t(f.label)}</option>)}
                    </select>
                    <select className="sp-input col-span-3 !py-1 text-xs" value={c.op} onChange={(e) => updateCond(i, { op: e.target.value })}>
                      {OPS.map((o) => <option key={o.key} value={o.key}>{i18n.t(o.label)}</option>)}
                    </select>
                    {!["exists", "not_exists"].includes(c.op) && (
                      <input className="sp-input col-span-3 !py-1 text-xs" placeholder="valore" value={c.value ?? ""} onChange={(e) => updateCond(i, { value: e.target.value })} />
                    )}
                    <button type="button" onClick={() => removeCond(i)} className="col-span-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)] justify-self-end"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sp-card p-5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold flex items-center gap-1"><Wand2 className="h-3.5 w-3.5" /> Allora esegui…</label>
              <button type="button" data-testid="add-action-btn" onClick={addAction} className="text-xs font-bold text-[color:var(--sp-brand)]">+ Aggiungi azione</button>
            </div>
            <div className="mt-3 space-y-3">
              {actions.map((a, i) => (
                <ActionRow key={i} index={i} action={a} tags={tags}
                  onUpdate={(patch) => updateAction(i, patch)}
                  onRemove={() => removeAction(i)} />
              ))}
            </div>
          </div>
        </div>

        {/* Flow tree visual */}
        <div className="lg:col-span-2">
          <div className="sticky top-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-2">Anteprima flusso</div>
            <FlowTree triggerType={triggerType} conditions={conditions} actions={actions} />
          </div>
        </div>
      </div>

      {/* Cronologia attività — solo dopo il primo salvataggio */}
      {savedId && (
        <section className="sp-card p-6" data-testid="automation-activity-log">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-[color:var(--sp-brand)]" />
              <h4 className="font-display text-lg font-black">Cronologia attività</h4>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)] px-2 py-0.5 rounded-full bg-[color:var(--sp-subtle)]">
                {jobs.length} eventi
              </span>
            </div>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-bold cursor-pointer">
                <input type="checkbox" checked={showOnlyErrors} onChange={(e) => setShowOnlyErrors(e.target.checked)}
                  data-testid="automation-activity-only-errors" className="h-3.5 w-3.5" />
                Solo errori
              </label>
              <button type="button" onClick={loadJobs} disabled={jobsLoading}
                data-testid="automation-activity-refresh"
                className="text-xs font-bold text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)] inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[color:var(--sp-subtle)]">
                <RefreshCw className={`h-3 w-3 ${jobsLoading ? "animate-spin" : ""}`} /> Aggiorna
              </button>
            </div>
          </div>
          {jobsLoading && jobs.length === 0 ? (
            <div className="py-8 text-center text-xs text-[color:var(--sp-text-muted)] inline-flex items-center gap-2 w-full justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Carico…
            </div>
          ) : (() => {
            const visible = showOnlyErrors
              ? jobs.filter((j) => j.status === "failed" || j.status === "dead")
              : jobs;
            if (visible.length === 0) {
              return (
                <div className="py-12 text-center text-sm text-[color:var(--sp-text-muted)]">
                  {showOnlyErrors
                    ? "Nessun errore registrato — l'automazione sta girando bene 🎉"
                    : "Nessuna esecuzione registrata. Prova l'automazione dal pulsante \"Test\" in alto o attendi il trigger."}
                </div>
              );
            }
            return (
              <ul className="divide-y divide-[color:var(--sp-border)] rounded-xl border border-[color:var(--sp-border)] bg-white overflow-hidden">
                {visible.map((j) => {
                  const isDone = j.status === "done";
                  const isFailed = j.status === "failed" || j.status === "dead";
                  const isRunning = j.status === "running";
                  const StatusIcon = isDone ? CheckCircle2 : isFailed ? AlertCircle : Clock;
                  const statusClass = isDone ? "text-emerald-600 bg-emerald-50"
                    : isFailed ? "text-rose-600 bg-rose-50"
                    : isRunning ? "text-blue-600 bg-blue-50"
                    : "text-amber-600 bg-amber-50";
                  const canExpand = isFailed || j.result || j.last_error || j.config;
                  const expanded = expandedJob === j.id;
                  return (
                    <li key={j.id} data-testid={`activity-job-${j.id}`}
                        className="p-4 hover:bg-[color:var(--sp-subtle)]/40 transition-colors">
                      <div className={`flex items-start gap-3 ${canExpand ? "cursor-pointer" : ""}`}
                           onClick={() => canExpand && setExpandedJob(expanded ? null : j.id)}>
                        <span className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${statusClass}`}>
                          <StatusIcon className="h-4 w-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm">
                              {j.action_type || "azione"}
                            </span>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${statusClass}`}>
                              {j.status}
                            </span>
                            {j.attempt > 1 && (
                              <span className="text-[10px] text-amber-700 font-bold">tentativo {j.attempt}</span>
                            )}
                          </div>
                          <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>{new Date(j.updated_at || j.created_at).toLocaleString("it-IT")}</span>
                            {j.client_name && <span>· cliente: <b className="text-[color:var(--sp-text)]">{j.client_name}</b></span>}
                            {j.duration_ms != null && <span>· {j.duration_ms}ms</span>}
                          </div>
                          {isFailed && j.last_error && !expanded && (
                            <div className="text-[11px] text-rose-700 mt-1 truncate font-medium">
                              ✕ {j.last_error}
                            </div>
                          )}
                        </div>
                        {canExpand && (
                          <span className="text-[10px] font-bold text-[color:var(--sp-text-muted)] flex-shrink-0 pt-1">
                            {expanded ? "Chiudi" : "Dettagli"}
                          </span>
                        )}
                      </div>
                      {expanded && (
                        <div className="mt-3 ml-11 p-3 rounded-lg bg-[color:var(--sp-subtle)]/60 text-[11px] space-y-3">
                          {j.last_error && (
                            <div>
                              <div className="font-bold text-rose-700 mb-1 uppercase tracking-wider text-[10px]">Errore</div>
                              <pre className="whitespace-pre-wrap text-rose-900 leading-relaxed font-mono text-[11px] bg-rose-50 p-2 rounded">{j.last_error}</pre>
                            </div>
                          )}
                          {j.result != null && (
                            <div>
                              <div className="font-bold text-[color:var(--sp-text-secondary)] mb-1 uppercase tracking-wider text-[10px]">Risultato</div>
                              <pre className="whitespace-pre-wrap text-[color:var(--sp-text)] leading-relaxed font-mono text-[11px] bg-white p-2 rounded border border-[color:var(--sp-border)]">{typeof j.result === "string" ? j.result : JSON.stringify(j.result, null, 2)}</pre>
                            </div>
                          )}
                          {j.config && (
                            <div>
                              <div className="font-bold text-[color:var(--sp-text-secondary)] mb-1 uppercase tracking-wider text-[10px]">Config azione</div>
                              <pre className="whitespace-pre-wrap text-[color:var(--sp-text-muted)] leading-relaxed font-mono text-[11px] bg-white p-2 rounded border border-[color:var(--sp-border)]">{JSON.stringify(j.config, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </section>
      )}
    </div>
  );
}

/* --- Trigger-specific filter panels (kept as-is for feature parity) --- */
function TriggerFilters({ filter, value, onChange, tags }) {
  if (filter === "client_source") {
    const sources = value.sources || [];
    const OPTIONS = ["ads", "facebook", "instagram", "form", "referral", "walk-in", "web"];
    const toggle = (s) => onChange({ ...value, sources: sources.includes(s) ? sources.filter((x) => x !== s) : [...sources, s] });
    return (
      <div className="mt-4 rounded-xl bg-[color:var(--sp-brand)]/5 border border-[color:var(--sp-brand)]/20 p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-secondary)] mb-2">Filtra fonti (opzionale)</div>
        <div className="flex flex-wrap gap-1.5" data-testid="trigger-filter-sources">
          {OPTIONS.map((s) => (
            <button key={s} type="button" onClick={() => toggle(s)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${sources.includes(s) ? "bg-[color:var(--sp-brand)] text-white border-[color:var(--sp-brand)]" : "bg-white text-[color:var(--sp-text-secondary)] border-[color:var(--sp-border)]"}`}>{s}</button>
          ))}
        </div>
        <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-2">Vuoto = tutte le fonti.</div>
      </div>
    );
  }
  if (filter === "tag") {
    return (
      <div className="mt-4 rounded-xl bg-[color:var(--sp-brand)]/5 border border-[color:var(--sp-brand)]/20 p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-secondary)] mb-2">Quale tag scatena l&apos;automazione?</div>
        <select data-testid="trigger-filter-tag" className="sp-input text-xs" value={value.tag_name || ""} onChange={(e) => onChange({ ...value, tag_name: e.target.value })}>
          <option value="">— Qualsiasi tag —</option>
          {tags.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
      </div>
    );
  }
  if (filter === "invoice") {
    return (
      <div className="mt-4 rounded-xl bg-[color:var(--sp-brand)]/5 border border-[color:var(--sp-brand)]/20 p-3 grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-secondary)] mb-1">Importo minimo (€)</div>
          <input type="number" data-testid="trigger-filter-min-amount" className="sp-input text-xs" placeholder="0" value={value.min_amount ?? ""} onChange={(e) => onChange({ ...value, min_amount: e.target.value ? Number(e.target.value) : undefined })} />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-secondary)] mb-1">Importo massimo (€)</div>
          <input type="number" className="sp-input text-xs" placeholder="illimitato" value={value.max_amount ?? ""} onChange={(e) => onChange({ ...value, max_amount: e.target.value ? Number(e.target.value) : undefined })} />
        </div>
      </div>
    );
  }
  if (filter === "appointment") {
    return <div className="mt-4 rounded-xl bg-[color:var(--sp-brand)]/5 border border-[color:var(--sp-brand)]/20 p-3 text-[11px] text-[color:var(--sp-text-muted)]">Suggerimento: usa le condizioni per filtrare per servizio specifico o staff.</div>;
  }
  if (filter === "inactive_days") {
    return (
      <div className="mt-4 rounded-xl bg-[color:var(--sp-brand)]/5 border border-[color:var(--sp-brand)]/20 p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-secondary)] mb-1">Dopo quanti giorni di inattività?</div>
        <input type="number" data-testid="trigger-filter-days" min="1" className="sp-input text-xs w-32" value={value.days || 60} onChange={(e) => onChange({ ...value, days: Number(e.target.value) })} />
      </div>
    );
  }
  return null;
}

function ActionRow({ index, action, onUpdate, onRemove, tags }) {
  const A = ACTION_MAP[action.type] || ACTION_MAP.log;
  const AIcon = A.icon;

  return (
    <div className="p-3 rounded-xl border border-[color:var(--sp-border)] bg-white space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold text-[color:var(--sp-text-muted)]">#{index + 1}</span>
        <span className="h-7 w-7 rounded-lg bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] flex items-center justify-center flex-shrink-0">
          <AIcon className="h-3.5 w-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-xs">{i18n.t(A.label)}</div>
          <div className="text-[10px] text-[color:var(--sp-text-muted)]">{i18n.t(A.desc)}</div>
        </div>
        <button type="button" onClick={onRemove} className="text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"><X className="h-3 w-3" /></button>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5" data-testid={`action-type-grid-${index}`}>
        {ACTIONS.map((opt) => {
          const OIcon = opt.icon;
          const active = opt.key === action.type;
          return (
            <button key={opt.key} type="button" data-testid={`action-opt-${index}-${opt.key}`}
              onClick={() => onUpdate({ type: opt.key, config: {} })}
              className={`p-2 rounded-lg border text-center transition-all ${active ? "border-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/5" : "border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]/40"}`}
            >
              <OIcon className={`h-3.5 w-3.5 mx-auto ${active ? "text-[color:var(--sp-brand)]" : "text-[color:var(--sp-text-muted)]"}`} />
              <div className="text-[9px] font-bold mt-1 truncate">{i18n.t(opt.label)}</div>
            </button>
          );
        })}
      </div>

      {action.type === "wait" && (
        <>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-bold">Aspetta</label>
            <input type="number" min="0" data-testid={`wait-value-${index}`} className="sp-input !py-1 text-xs w-24" value={action.delay_value ?? 30} onChange={(e) => onUpdate({ delay_value: Number(e.target.value), delay_minutes: 0 })} />
            <select data-testid={`wait-unit-${index}`} className="sp-input !py-1 text-xs w-32" value={action.delay_unit || "minutes"} onChange={(e) => onUpdate({ delay_unit: e.target.value, delay_minutes: 0 })}>
              <option value="seconds">secondi</option>
              <option value="minutes">minuti</option>
              <option value="hours">ore</option>
              <option value="days">giorni</option>
            </select>
          </div>
          {action.delay_unit === "seconds" && (action.delay_value || 0) < 60 && (
            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">⚠ Sotto i 60 secondi non è garantito: il worker gira a granularità di minuto.</div>
          )}
        </>
      )}
      {action.type === "add_tag" && (
        <div>
          <label className="text-[11px] font-bold">Tag da applicare al cliente</label>
          <div className="mt-1 flex gap-2">
            <select data-testid={`add-tag-select-${index}`} className="sp-input !py-1 text-xs flex-1" value={action.config?.tag_name || ""} onChange={(e) => onUpdate({ config: { ...action.config, tag_name: e.target.value } })}>
              <option value="">— Scegli tag esistente —</option>
              {tags.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
            <input className="sp-input !py-1 text-xs flex-1" placeholder="oppure crea nuovo" value={action.config?.tag_name || ""} onChange={(e) => onUpdate({ config: { ...action.config, tag_name: e.target.value } })} />
          </div>
        </div>
      )}
      {action.type === "add_note" && (
        <input className="sp-input !py-1 text-xs" placeholder="Es. ⭐ Cliente VIP" value={action.config?.note || ""} onChange={(e) => onUpdate({ config: { ...action.config, note: e.target.value } })} />
      )}
      {action.type === "send_whatsapp" && (
        <div>
          <input className="sp-input !py-1 text-xs" placeholder="Nome template WhatsApp (es. reminder_24h)" value={action.config?.template_name || ""} onChange={(e) => onUpdate({ config: { ...action.config, template_name: e.target.value } })} />
          <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">⚠ Invio verso il cliente del contesto. STUB fino ad attivazione Meta Cloud API.</div>
        </div>
      )}
      {action.type === "send_email" && (
        <EmailActionConfig config={action.config || {}} onChange={(c) => onUpdate({ config: c })} />
      )}
      {action.type === "log" && (
        <input className="sp-input !py-1 text-xs" placeholder="Messaggio log (debug)" value={action.config?.message || ""} onChange={(e) => onUpdate({ config: { ...action.config, message: e.target.value } })} />
      )}

      {action.type !== "wait" && (
        <div className="flex items-center gap-2 pt-1 border-t border-[color:var(--sp-border)]/40">
          <label className="text-[10px] font-bold text-[color:var(--sp-text-muted)]">Ritardo prima di eseguire:</label>
          <input type="number" min="0" className="sp-input !py-1 text-xs w-16" value={action.delay_value ?? 0} onChange={(e) => onUpdate({ delay_value: Number(e.target.value) })} />
          <select className="sp-input !py-1 text-xs w-24" value={action.delay_unit || "minutes"} onChange={(e) => onUpdate({ delay_unit: e.target.value })}>
            <option value="seconds">sec</option>
            <option value="minutes">min</option>
            <option value="hours">ore</option>
            <option value="days">giorni</option>
          </select>
        </div>
      )}
    </div>
  );
}

function EmailActionConfig({ config, onChange }) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/email/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-bold">Template email da inviare</label>
      <select className="sp-input !py-1 text-xs" value={config.template_id || ""} onChange={(e) => onChange({ ...config, template_id: e.target.value })}>
        <option value="">— Scegli template —</option>
        {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <div className="text-[10px] text-[color:var(--sp-text-muted)]">Il template contiene oggetto e HTML: nessuna configurazione extra necessaria.</div>
    </div>
  );
}
